import { Delete } from '@mui/icons-material';
import {
	Box,
	CircularProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { StudentInterface } from '../../interfaces';
import { useStudentService, useTranslation } from '../../services';
import { ButtonWithNotes } from '../ButtonWithNotes';
import { EditStudentDialog } from '../EditStudentDialog';
import { Status } from '../Status';

const InnerTableStudents: FC<{ request: string }> = ({ request }) => {
	const { t } = useTranslation();
	const { students, getStudents, deleteStudent, updateStudent, createStudent } =
		useStudentService();
	const [relatedStudents, setRelatedStudents] = useState<StudentInterface[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [countTry, setCountTry] = useState<number>(0);

	useEffect(() => {
		const reqStudents = students.filter((student) => student.requestId === request);
		if (countTry < 3 && reqStudents.length === 0) {
			(async () => {
				setLoading(true);
				await getStudents({ request });
				setLoading(false);
				setCountTry(countTry + 1);
			})();
		} else {
			setRelatedStudents(reqStudents);
		}
	}, [students, countTry]);

	const handleDelete = useCallback(
		async (val: boolean, notes?: string, id?: string) => {
			val && id && (await deleteStudent(id, notes));
		},
		[deleteStudent]
	);

	const handleStudentEdit = useCallback(
		async (data: StudentInterface, id?: string) => {
			if (id) {
				await updateStudent(id, data);
			}
		},
		[updateStudent]
	);

	const handleStudentNew = useCallback(
		async (data: StudentInterface) => {
			setLoading(true);
			await createStudent(data, request);
			setLoading(false);
		},
		[createStudent]
	);

	return (
		<Box sx={{ m: 1, flexGrow: 1 }}>
			<Stack direction="row" spacing={2} alignItems="center">
				<Typography variant="h6" margin={0} gutterBottom component="div">
					{t('Students')}
				</Typography>
				<EditStudentDialog
					mode="create"
					update={(i, res) => handleStudentNew(res)}
					index={0}
					value={{ name: '', email: '', notes: '' }}
				/>
			</Stack>

			{loading ? (
				<Stack direction="row" justifyContent="center" alignItems="center">
					<CircularProgress color="inherit" sx={{ m: 1 }} />
					{t('Please wait...')}
				</Stack>
			) : (
				<Table size="small" aria-label="purchases">
					<TableHead>
						<TableRow>
							<TableCell>{t('Name')}</TableCell>
							<TableCell>{t('Email')}</TableCell>
							<TableCell>{t('Status')}</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{relatedStudents?.map((student, index) => (
							<TableRow key={student.id}>
								<TableCell component="th" scope="row">
									{student.name}
								</TableCell>
								<TableCell>{student.email}</TableCell>
								<TableCell>
									<Status status={student.status} />
								</TableCell>
								<TableCell>
									{student.id && (
										<EditStudentDialog
											update={(i, res) =>
												handleStudentEdit(res, student.id)
											}
											value={student}
											index={index}
										/>
									)}
									<ButtonWithNotes
										WrappedComponent={Delete}
										callback={(val, notes) =>
											handleDelete(val, notes, student.id)
										}
										propsWrapped={{
											title: t('Remove student'),
											color: 'error',
										}}
									/>
								</TableCell>
							</TableRow>
						))}
						{relatedStudents.length === 0 && countTry === 3 && (
							<TableRow>
								<TableCell colSpan={4}>
									<Stack alignItems="center" justifyContent="center">
										<Box>{t('Not Found')}</Box>
									</Stack>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			)}
		</Box>
	);
};

export default InnerTableStudents;
